// filepath: apps/web/src/features/lists/hooks/useKeysetPagination.ts
//
// FR-UI-LIST-1 AC-3 — cursor-derivation hook for the list view.
//
// Produces the next-page query params given the server's
// `next_cursor` and the active filter + sort. The hook enforces the
// AC-3 invariant: filter+sort survive cursor advances — they live
// in URL query params, NOT in the cursor; the cursor encodes only
// the keyset anchor.
//
// ARCHITECTURE_WEB.md §5: pagination is keyset-only (`next_cursor`).
// No offset pagination. The Next button is enabled iff
// `next_cursor !== ""`; no Previous button (keyset is forward-only).

import { useCallback, useMemo } from "react";
import type { EntityListFilter, EntityListSort } from "../types";

export interface UseKeysetPaginationArgs {
  /** Active filter (URL-derived). */
  filter: EntityListFilter[];
  /** Active sort (URL-derived). */
  sort: EntityListSort[];
  /** Current cursor. `""` or `null` = first page. */
  cursor: string | null;
  /**
   * The server's `next_cursor` for the current page. When `""`,
   * there is no next page (FR-QRY-1 AC-4).
   */
  nextCursor: string;
}

export interface NextPageParams {
  filter: EntityListFilter[];
  sort: EntityListSort[];
  cursor: string;
}

export interface UseKeysetPaginationResult {
  /** True iff the next button should be enabled. */
  hasNextPage: boolean;
  /**
   * Produce the next-page query params. Filter+sort are forwarded
   * unchanged (AC-3 invariant); the cursor advances to
   * `nextCursor`. `null` when there is no next page — callers
   * should disable the button via `hasNextPage` before reaching
   * this branch.
   */
  buildNextPageParams: () => NextPageParams | null;
  /**
   * The raw next cursor from the server. Exposed for the
   * PaginationBar to use as the `disabled` predicate and for tests.
   */
  rawNextCursor: string;
  /**
   * Snapshot of the next-page params without building them — useful
   * for render-time decision-making. `null` when no next page.
   */
  nextPageParams: NextPageParams | null;
}

/**
 * useKeysetPagination — derives the next-page query params from the
 * active cursor + filter + sort. See file header.
 */
export function useKeysetPagination(
  args: UseKeysetPaginationArgs,
): UseKeysetPaginationResult {
  const { filter, sort, nextCursor } = args;

  const hasNextPage = useMemo(
    () => typeof nextCursor === "string" && nextCursor.length > 0,
    [nextCursor],
  );

  const nextPageParams = useMemo<NextPageParams | null>(() => {
    if (!hasNextPage) return null;
    return {
      filter,
      sort,
      cursor: nextCursor,
    };
    // The cursor-from-page index is intentionally NOT part of
    // `nextPageParams` — the next-page filter+sort persist
    // verbatim from the active page (AC-3 invariant). The cursor
    // advances from `nextCursor` (the server's keyset anchor for
    // the CURRENT page), which the next-page hook call uses.
  }, [hasNextPage, filter, sort, nextCursor]);

  const buildNextPageParams = useCallback((): NextPageParams | null => {
    if (!hasNextPage) return null;
    return {
      filter,
      sort,
      cursor: nextCursor,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextPage, filter, sort, nextCursor]);

  return {
    hasNextPage,
    buildNextPageParams,
    rawNextCursor: nextCursor,
    nextPageParams,
  };
}
